import { useAuth as useClerkAuth } from "@clerk/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { useMemo, useState, type ReactNode } from "react";

import { API_BASE, ApiAccessProvider } from "./api";
import { trpc } from "./trpc";

interface TrpcProviderProps {
  children: ReactNode;
}

function useBuildClients(getToken: (() => Promise<string | null>) | null) {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${API_BASE}/trpc`,
          async headers() {
            if (!getToken) {
              return {};
            }
            const token = await getToken();
            return token ? { Authorization: `Bearer ${token}` } : {};
          },
        }),
      ],
    }),
  );
  return { queryClient, trpcClient };
}

/**
 * tRPC + react-query provider for the admin dashboard. Pulls the
 * Clerk session token and passes it as a Bearer header so backend
 * admin procedures can verify against the dedicated admin Clerk app.
 */
export function TrpcProvider({ children }: TrpcProviderProps) {
  const { getToken } = useClerkAuth();
  const { queryClient, trpcClient } = useBuildClients(getToken);
  const access = useMemo(() => ({ getToken }), [getToken]);

  return (
    <ApiAccessProvider value={access}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      </trpc.Provider>
    </ApiAccessProvider>
  );
}

/**
 * Dev-bypass variant — NO Clerk hooks anywhere in this tree (they
 * throw without a ClerkProvider). Requests carry no Authorization
 * header; the local Worker's localhost-gated bypass supplies the
 * admin identity. Only reachable when DEV_BYPASS is true, which is
 * compile-time false in production builds.
 */
export function DevBypassTrpcProvider({ children }: TrpcProviderProps) {
  const { queryClient, trpcClient } = useBuildClients(null);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
